import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import {
  customerSchema,
  type CustomerFormData,
} from "@/schemas/customer.schema";
import { useCreateCustomer, useUpdateCustomer } from "../hooks/useCustomers";
import { useCustomersDetail } from "../hooks/useCustomers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useEffect } from "react";
import toast from "react-hot-toast";
import Loading from "@/components/fragments/Loadin";

export default function CustomerForm({ id, email }: any) {
  const navigate = useNavigate();
  const isEditMode = !!id;

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
  });

  // Fetch detail jika edit mode
  const { data: customerDetail, isLoading: isLoadingDetail } =
    useCustomersDetail({
      email,
      id: Number(id),
    });

  // Mutations
  const createMutation = useCreateCustomer(email);
  const updateMutation = useUpdateCustomer(email, Number(id));

  // Populate form jika edit mode
  useEffect(() => {
    if (isEditMode && customerDetail) {
      reset({
        cust_name: customerDetail.cust_name,
        cust_nik: customerDetail.cust_nik,
        cust_address: customerDetail.cust_address,
        cust_email: customerDetail.cust_email || "",
        cust_phone: customerDetail.cust_phone,
      });
    }
  }, [customerDetail, isEditMode, reset]);

  // Submit Handler
  const onSubmit = async (data: CustomerFormData) => {
    try {
      if (isEditMode) {
        await updateMutation.mutateAsync(data);
        toast.success("Customer berhasil diupdate!");
      } else {
        await createMutation.mutateAsync(data);
        toast.success("Customer berhasil dibuat!");
      }
      navigate("/customer");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Terjadi kesalahan");
    }
  };

  // Loading state untuk edit mode
  if (isEditMode && isLoadingDetail) {
    return (
      <Loading fullScreen message="Loading Data Customer..." size="large" />
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="container mx-auto px-4 pt-4  pb-8">
      {/* Header */}
      <div className="bg-[#f4f2fc] dark:bg-[#1b1b1b] rounded-3xl p-8 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
            <span className="text-3xl">{isEditMode ? "✏️" : "➕"}</span>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#1e1e1e] dark:text-white">
              {isEditMode ? "Edit Customer" : "Tambah Customer Baru"}
            </h1>
            <p className="text-[#37393d]  mt-1 dark:text-white">
              {isEditMode
                ? "Update informasi customer"
                : "Isi form untuk membuat customer baru"}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Nama Customer */}
        <div className=" rounded-2xl  hover:bg-transparent transition-colors">
          <Label
            htmlFor="cust_name"
            className="text-[#37393d] dark:text-white font-medium mb-2 block"
          >
            Nama Customer <span className="text-[#ff7075]">*</span>
          </Label>
          <Input
            id="cust_name"
            {...register("cust_name")}
            placeholder="Masukkan nama customer"
            className={`bg-white dark:bg-[#1b1b1b] border-2 ${
              errors.cust_name ? "border-[#ff7075]" : "border-[#efecff]"
            } focus:border-[#d7d0fe] text-[#1e1e1e]`}
          />
          {errors.cust_name && (
            <p className="text-[#ff7075] text-sm mt-1">
              {errors.cust_name.message}
            </p>
          )}
        </div>

        {/* NIK */}
        <div className=" rounded-2xl  hover:bg-transparent transition-colors">
          <Label
            htmlFor="cust_nik"
            className="text-[#37393d] dark:text-white font-medium mb-2 block"
          >
            NIK <span className="text-[#ff7075]">*</span>
          </Label>
          <Input
            id="cust_nik"
            {...register("cust_nik")}
            placeholder="Masukkan NIK (16 digit)"
            maxLength={16}
            className={`bg-white border-2 ${
              errors.cust_nik ? "border-[#ff7075]" : "border-[#efecff]"
            } focus:border-[#d7d0fe] text-[#1e1e1e]`}
          />
          {errors.cust_nik && (
            <p className="text-[#ff7075] text-sm mt-1">
              {errors.cust_nik.message}
            </p>
          )}
        </div>

        {/* Alamat */}
        <div className=" rounded-2xl  hover:bg-transparent transition-colors">
          <Label
            htmlFor="cust_address"
            className="text-[#37393d] dark:text-white font-medium mb-2 block"
          >
            Alamat <span className="text-[#ff7075]">*</span>
          </Label>
          <Textarea
            id="cust_address"
            {...register("cust_address")}
            placeholder="Masukkan alamat lengkap"
            rows={3}
            className={`bg-white border-2 ${
              errors.cust_address ? "border-[#ff7075]" : "border-[#efecff]"
            } focus:border-[#d7d0fe] text-[#1e1e1e] resize-none`}
          />
          {errors.cust_address && (
            <p className="text-[#ff7075] text-sm mt-1">
              {errors.cust_address.message}
            </p>
          )}
        </div>

        {/* Email */}
        <div className=" rounded-2xl  hover:bg-transparent transition-colors">
          <Label
            htmlFor="cust_email"
            className="text-[#37393d] dark:text-white font-medium mb-2 block"
          >
            Email{" "}
            <span className="text-[#37393d] dark:text-white text-sm">
              (Opsional)
            </span>
          </Label>
          <Input
            id="cust_email"
            type="email"
            {...register("cust_email")}
            placeholder="Masukkan email"
            className={`bg-white border-2 ${
              errors.cust_email ? "border-[#ff7075]" : "border-[#efecff]"
            } focus:border-[#d7d0fe] text-[#1e1e1e]`}
          />
          {errors.cust_email && (
            <p className="text-[#ff7075] text-sm mt-1">
              {errors.cust_email.message}
            </p>
          )}
        </div>

        {/* No. Telepon */}
        <div className=" rounded-2xl  hover:bg-transparent transition-colors">
          <Label
            htmlFor="cust_phone"
            className="text-[#37393d] dark:text-white font-medium mb-2 block"
          >
            No. Telepon <span className="text-[#ff7075]">*</span>
          </Label>
          <Input
            id="cust_phone"
            {...register("cust_phone")}
            placeholder="Masukkan nomor telepon"
            className={`bg-white border-2 ${
              errors.cust_phone ? "border-[#ff7075]" : "border-[#efecff]"
            } focus:border-[#d7d0fe] text-[#1e1e1e]`}
          />
          {errors.cust_phone && (
            <p className="text-[#ff7075] text-sm mt-1">
              {errors.cust_phone.message}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="cursor-pointer bg-[#d7d0fe] text-[#1e1e1e] hover:bg-[#c5bcfe] font-semibold flex-1"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-[#1e1e1e] border-t-transparent rounded-full animate-spin mr-2"></div>
                {isEditMode ? "Menyimpan..." : "Membuat..."}
              </>
            ) : (
              <>{isEditMode ? "💾 Simpan Perubahan" : "✅ Buat Customer"}</>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/customer")}
            className="cursor-pointer border-[#37393d] text-[#37393d] dark:text-white hover: font-semibold"
          >
            ❌ Batal
          </Button>
        </div>
      </form>
    </div>
  );
}
